import { useAmp } from "next/amp"
import { Fragment, useEffect } from "react"
import { observer } from "mobx-react"
import classnames from "classnames"
import { Footer, Header, PureLogo, Page, useStore } from "@features/common"
import { ApplicationBanner, Article, ArticleAuthor } from "@features/journal"
import { SaferDatingLogoEN } from "@features/safer-dating"
import { scrollToTop } from "@lib/scroll"
import { RouterLink } from "@lib/routing"
import { ellipsisText } from "@lib/string"
import { IconHamburger } from "@lib/icons"
import { useSmartBanner } from "@features/smart-banner"
import { ArticleShare } from "@features/share"
import { modelsInit } from "@models"

import type { GetStaticProps, GetStaticPaths } from "next"
import type {
  CountryCodePathValues,
  SmartBannerPathValues,
  AMPPathValues,
} from "lib/isr"
import type { LanguageISO } from "@providers"

export const config = {
  amp: "hybrid",
}

type PostParsedUrlQuery = {
  lang?: LanguageISO
  slug: string
  countryCode: CountryCodePathValues
  smartBanner: SmartBannerPathValues
  ampValue: AMPPathValues
}

export const getStaticProps: GetStaticProps<{}, PostParsedUrlQuery> = async (
  context,
) => {
  const Models = modelsInit()
  const {
    slug = "",
    lang = "en",
    countryCode = "ru",
    ampValue = "full",
    smartBanner = "smart-banner-enabled",
  } = context.params || {}

  await Models.GlobalModel.getInitial()
  Models.GlobalModel.setPostFirstServerUrl({
    slug,
    lang,
    scope: "safer-dating",
    isAmp: ampValue === "amp",
  })

  // Forced, as on the Safer Dating list page: these articles exist in
  // languages the rest of the site does not offer, and without the flag the
  // language is dropped and the article is read in the default one.
  Models.GlobalModel.setLang(lang, true)

  try {
    Models.GlobalModel.setClientCountryCode(countryCode)
    await Models.SaferDatingModel.getPost(slug as string)
    await Models.BlogModel.readDefaultArticlesBanner()
    await Models.GlobalModel.getSmartBanner()

    if (smartBanner === "smart-banner-disabled") {
      Models.GlobalModel.setIsSmartBannerDisabled(true)
    }

    if (!Models.SaferDatingModel.postDetails) {
      return {
        redirect: {
          permanent: false,
          destination: `/${lang}/safer-dating`,
        },
      }
    }

    return {
      props: Models.serialize(),
      revalidate: Number(process.env.PAGE_REVALIDATE_TIMEOUT) || 30,
    }
  } catch (err) {
    console.log("GET STATIC PROPS POST ERROR", err) // eslint-disable-line
    return {
      redirect: {
        destination: `/${lang}/safer-dating`,
        permanent: false,
      },
    }
  }
}

export const getStaticPaths: GetStaticPaths<PostParsedUrlQuery> = async () => {
  return {
    paths: [],
    fallback: "blocking",
  }
}

const ArticlePage = observer(function ArticlePage() {
  const {
    GlobalModel,
    BlogModel: { branch_links },
    SaferDatingModel: {
      postDetails,
      relatedPostsBySlug,
      articleBanner,
      availableLanguages,
    },
  } = useStore()
  const isAmp = useAmp()
  const { smartBanner } = useSmartBanner()

  useEffect(() => {
    document.querySelector("main")?.scrollTo({
      top: 0,
    })
    //eslint-disable-next-line
  }, [postDetails?.id])

  if (!postDetails) {
    return null
  }

  const pageDescription =
    postDetails.meta_description || ellipsisText(postDetails.description)
  const alternateLinks = relatedPostsBySlug.map(({ lang, slug }) => ({
    lang,
    url: `${GlobalModel.originUrl || ""}/${lang}/safer-dating/${slug}`,
  }))

  // The same list the Safer Dating list page offers, so a reader can switch to
  // any language the page exists in. Without it the switcher falls back to the
  // site-wide list, which leaves out Italian, Ukrainian and Spanish. Switching
  // to a language this article is missing lands on that language's list page.
  const pageLanguages = availableLanguages.length
    ? availableLanguages
    : GlobalModel.accessLanguages

  const lastSectionBackground =
    /* eslint-disable @typescript-eslint/no-unnecessary-condition */
    postDetails.elements[postDetails.elements.length - 1]?.background || "white"

  const metaImage = postDetails.image_preview.url || postDetails.image_cover.url

  return (
    <Page
      meta={{
        title: postDetails.title,
        metaTitle: postDetails.meta_title || postDetails.title,
        description: pageDescription,
        metaImage,
        metaPublishedTime: postDetails.published_at,
        metaAuthor: postDetails.author?.name,
        metaAuthorImg: postDetails.author?.image,
        isArticle: true,
        alternateLinks,
      }}
      navigation={isAmp ? null : undefined}
      smartBanner={smartBanner}
      header={
        <Header
          theme="dark"
          navigationOpener={
            <button
              onClick={GlobalModel.openMenu}
              data-test-id="header__menu_button"
              aria-label="menu"
            >
              <IconHamburger className="w-8 h-8 text-white" />
            </button>
          }
          languageSwitcher={null}
          logo={
            <RouterLink
              uri="/safer-dating"
              forceLang={GlobalModel.lang}
              onClick={() => scrollToTop()}
              data-test-id="header__pure_logo"
              aria-label="root"
              className="mt-4 mb-3"
            >
              <div className="flex space-x-2 tablet-m:space-x-4">
                <PureLogo className="text-white h-4 w-auto my-1 tablet:w-auto tablet-m:h-7 tablet:h-7 tablet-m:my-1.5" />
                <SaferDatingLogoEN />
              </div>
            </RouterLink>
          }
        />
      }
      alternativeHeader
      footer={
        <Footer
          googlePlayLink={branch_links.main_footer_google_play}
          appStoreLink={branch_links.main_footer_app_store}
          appGalleryLink={branch_links.main_footer_app_gallery}
          accessLanguages={pageLanguages}
        />
      }
    >
      <Article article={postDetails} source="safer_dating" />
      <div
        className={classnames({
          "bg-white": lastSectionBackground === "white",
          "bg-black text-white": lastSectionBackground === "black",
          "bg-gray-50": lastSectionBackground === "gray",
        })}
      >
        {postDetails.author && <ArticleAuthor author={postDetails.author} />}
        <ArticleShare background={lastSectionBackground} />
      </div>
      {isAmp ? null : (
        <Fragment>
          {articleBanner ? (
            <div className="desktop:max-w-desktop-l desktop:pt-20 desktop:pb-10 desktop:px-20 desktop:mx-auto">
              <ApplicationBanner {...articleBanner} />
            </div>
          ) : null}
        </Fragment>
      )}
    </Page>
  )
})

export default ArticlePage
